import styled from "@emotion/styled";
import {
  Divider,
  Grid
} from "@mui/material";
import { blue, black } from "../color";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  ColoredButton,
  CustomFlex,
  CustomButton,
  Paragraph,
} from "./Layout.elements";
import TableGridSection from "./TableGridSection";
import ScrollableTable from "./ScrollableTable";

const { blackPearl } = black;
const linkColor = blue[900];

const Container = styled.div`
  margin-top: 60px;
  padding: 0px 5rem;
  @media only screen and (max-width: 768px) {
    padding: 0 1.5rem;
    margin-top: 0px;
  }
`;

const Para = styled(Paragraph)`
  width: 40%;
  @media only screen and (max-width: 768px) {
    width: 100%;
    font-size: 2.125rem;
    margin-top: 0;
  }
`;

const ParagraphRow = styled.div`
  display: flex;
  gap: 5rem;
  align-items: flex-start;
  @media only screen and (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const NumberedGrid1 = styled(Grid)`
  margin-right: 9.25rem;
  padding-left: 2.5rem;
  width: 100%;
  @media (max-width: 768px) {
    margin-right: 0;
    padding-left: 0;
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    margin-right: 0;
    padding-left: 0;
  }
`;

const NumberedGrid2 = styled(Grid)`
  margin-left: 9.25rem;
  padding-right: 2.5rem;
  @media only screen and (max-width: 768px) {
    margin-left: 0;
    padding-right: 0;
  }
`;

const Number = styled.h1`
  color: ${blue[900]};
  font-size: 8.5rem;
  margin: 0;
`;

const MiddleDivider = styled(Divider)`
  border-width: 0.0625rem;
  margin: 4rem 0;
  @media only screen and (max-width: 768px) {
    margin: 2.5rem 0;
  }
`;

const ResponsiveDivider = styled(Divider)`
  @media only screen and (max-width: 768px) {
    height: 0;
  }
`;

const SummaryGrid = styled(Grid)`
  margin: 4rem 0;
  @media only screen and (max-width: 768px) {
    margin: 0;
  }
`;

const FooterParagraph = styled.p`
  font-size: 2rem;
  font-weight: 300;
  margin-bottom: 4rem;
  margin-top: 0;
  @media only screen and (max-width: 768px) {
    font-size: 1.3125rem;
  }
`;

const AppCoderBtn = styled(ColoredButton)`
  text-transform: none;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;
const MiddleSection = ({
  setBlackScreen,
  queryIndex,
  setQueryIndex,
  setOpenAllResponseScreen,
  setQueryParams
}) => {
  const params = new URLSearchParams(window.location.search);
  
  const handleQueryIndex = (value) => {
    queryIndex(value);
  };
  const handleRedirect = () => {
    setBlackScreen(true);
    params.set("q", "new");
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setQueryParams(params.toString());
  }
  const handleAllResponse = () => {
    setBlackScreen(false);
    setOpenAllResponseScreen(true);
    params.set("q", "all");
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ path: newUrl }, '', newUrl);
    window.scrollTo({ top: 0, behavior: "smooth" });
    setQueryParams(params.toString());
  };
  return (
    <>
      <Container>
        <MiddleDivider />
        <Para>Scoring high on ICE benchmark test</Para>

        <ParagraphRow>
          <p className="section-paragraph">
            The ICE methodology provides metrics for Usefulness and Functional
            Correctness as a baseline for scoring code generation. Read more
            about the ICE methodology in this paper.
          </p>
          <p className="section-paragraph">
            We utilized GPT4 to measure the metrics and provided a score between
            0-4. This is the test dataset and Jupyter Notebook we used to
            perform the benchmark.
          </p>
        </ParagraphRow>

        <Grid container style={{ display: 'flex', justifyContent: 'center' }}>
          <NumberedGrid1 item md={4} xs={12}>
            <Number>2.9</Number>
            <h3 className="remove-top-margin">USEFULNESS</h3>
            <p className="resp-summary">
              Usefulness: addresses whether the code output from the model is
              clear, presented in a logical order, and maintains human
              readability and whether it covers all functionalities of the
              problem statement after comparing it with the reference code.
            </p>
          </NumberedGrid1>
          <Grid item md={1} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ResponsiveDivider orientation="vertical" flexItem />
          </Grid>
          <NumberedGrid2 item md={4} xs={12}>
            <Number>2.4</Number>
            <h3 className="remove-top-margin">FUNCTIONALITY</h3>
            <p className="resp-summary">
              Functional Correctness: An LLM that has complex reasoning
              capabilities is utilized to conduct unit tests while considering
              the given question and the reference code.
            </p>
          </NumberedGrid2>
        </Grid>

        <ScrollableTable />

        <MiddleDivider />

        <SummaryGrid container>
          <Grid item md={6} xs={12}>
            <Para style={{ width: "100%" }}>What we are doing</Para>
          </Grid>
          <Grid item md={6} xs={12}>
            <p className="summary">
              We have been fine-tuning CodeLlama -7B, 34B and Wizard Coder -15B,
              34B. We combined that fine-tuning with our hand-coded dataset
              training on LangChain, YOLO V8, VertexAI and many other modern
              libraries which we use on a daily basis. We fine-tuned our work on
              top of WizardCoder-15B.
            </p>
          </Grid>
        </SummaryGrid>

        <MiddleDivider />

        <CustomFlex direction="column" content="center" align="flex-start">
          <Para>Outputs generated using Interplay AppCoder LLM</Para>

          <CustomButton
            variant="text"
            size="medium"
            textColor={linkColor}
            endIcon={<ArrowForwardIcon />}
            onClick={handleAllResponse}
          >
            All Inputs & Answers
          </CustomButton>

          <TableGridSection
            queryIndex={handleQueryIndex}
            border={`2px solid ${blackPearl}`}
            setBlackScreen={setBlackScreen}
            setQueryIndex={setQueryIndex}
            setQueryParams={setQueryParams}
          />
        </CustomFlex>

        <CustomFlex content="center" className="btn-container">
          <AppCoderBtn
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleRedirect}
          >
            Try Interplay AppCoder LLM
          </AppCoderBtn>
        </CustomFlex>

        <MiddleDivider />

        <CustomFlex style={{ textAlign: "center", marginBottom: "5rem" }}>
          <FooterParagraph>
            Iterate is fully committed to continually improve and advance the AI
            toolset for enterprises. Interplay AppCoder LLM is a huge step
            forward.
          </FooterParagraph>
        </CustomFlex>
      </Container>
    </>
  );
};
export default MiddleSection;
